import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config/api';
import {
  AcademicCapIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  DocumentTextIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../contexts/SiteBrandingContext';
import TranslatableText from './TranslatableText';

const Footer = () => {
  const { isDark } = useTheme();
  const { siteBranding } = useSiteBranding();
  const [socialMediaLinks, setSocialMediaLinks] = useState([]);
  
  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const fetchSocialMedia = async () => {
    try {
      // Fetch social media links
      const socialResponse = await fetch(getApiUrl('/site/social-media'));
      if (socialResponse.ok) {
        const socialData = await socialResponse.json();
        setSocialMediaLinks(socialData);
      }
    } catch (error) {
      console.error('Error fetching social media links:', error);
    }
  };

  const getSocialMediaIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return 'f';
      case 'twitter': return 't';
      case 'instagram': return 'ig';
      case 'youtube': return 'yt';
      case 'linkedin': return 'in';
      default: return platform.charAt(0);
    }
  };

  const getSocialMediaColor = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return 'bg-blue-600 hover:bg-blue-700';
      case 'twitter': return 'bg-blue-400 hover:bg-blue-500';
      case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600';
      case 'youtube': return 'bg-red-600 hover:bg-red-700';
      case 'linkedin': return 'bg-blue-700 hover:bg-blue-800';
      default: return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  // Footer sections inspired by exeter.edu structure
  const aboutSchoolLinks = [
    { name: 'Mission & Values', href: '/vision-mission', icon: TrophyIcon },
    { name: 'Why Our School', href: '/about', icon: AcademicCapIcon },
    { name: 'Principal\'s Message', href: '/principal-message', icon: UserGroupIcon },
    { name: 'Our Campus', href: '/campus-tour', icon: BuildingLibraryIcon },
    { name: 'History & Legacy', href: '/history', icon: DocumentTextIcon }
  ];

  const academicsAdmissionsLinks = [
    { name: 'Curriculum Overview', href: '/academics', icon: AcademicCapIcon },
    { name: 'Subjects Offered', href: '/subjects', icon: DocumentTextIcon },
    { name: 'Admission Process', href: '/admissions', icon: UserGroupIcon },
    { name: 'Fee Structure', href: '/fee-structure', icon: DocumentTextIcon },
    { name: 'Online Application', href: '/apply-online', icon: AcademicCapIcon }
  ];

  const studentLifeLinks = [
    { name: 'Events & Activities', href: '/events', icon: TrophyIcon },
    { name: 'Sports & Games', href: '/sports', icon: TrophyIcon },
    { name: 'Clubs & Societies', href: '/clubs', icon: UserGroupIcon },
    { name: 'Cultural Programs', href: '/cultural', icon: TrophyIcon },
    { name: 'Student Council', href: '/student-council', icon: UserGroupIcon }
  ];

  const resourcesContactLinks = [
    { name: 'Downloads', href: '/downloads', icon: DocumentTextIcon },
    { name: 'Gallery', href: '/gallery', icon: BuildingLibraryIcon },
    { name: 'News & Updates', href: '/news', icon: DocumentTextIcon },
    { name: 'Contact Us', href: '/contact', icon: PhoneIcon },
    { name: 'Public Disclosure', href: '/public-disclosure', icon: DocumentTextIcon }
  ];

  return (
    <footer className={`${isDark ? 'bg-dark-surface text-dark-text' : 'bg-gray-50 text-gray-900'} border-t ${isDark ? 'border-dark-border' : 'border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* About School Section */}
          <div>
            <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-dark-text' : 'text-gray-900'} flex items-center`}>
              <AcademicCapIcon className="w-5 h-5 mr-2" style={{ color: siteBranding.primary_color }} />
              <TranslatableText text="About PM SHRI GGSSS FARMANA" />
            </h3>
            <div className="space-y-3">
              {aboutSchoolLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center text-sm ${isDark ? 'text-dark-text-secondary hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'} transition-colors hover:translate-x-1 transform duration-200`}
                >
                  <link.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                  <TranslatableText text={link.name} />
                </Link>
              ))}
            </div>
          </div>

          {/* Academics & Admissions Section */}
          <div>
            <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-dark-text' : 'text-gray-900'} flex items-center`}>
              <BuildingLibraryIcon className="w-5 h-5 mr-2" style={{ color: siteBranding.primary_color }} />
              <TranslatableText text="Academics & Admissions" />
            </h3>
            <div className="space-y-3">
              {academicsAdmissionsLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center text-sm ${isDark ? 'text-dark-text-secondary hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'} transition-colors hover:translate-x-1 transform duration-200`}
                >
                  <link.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                  <TranslatableText text={link.name} />
                </Link>
              ))}
            </div>
          </div>

          {/* Student Life & Activities Section */}
          <div>
            <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-dark-text' : 'text-gray-900'} flex items-center`}>
              <UserGroupIcon className="w-5 h-5 mr-2" style={{ color: siteBranding.primary_color }} />
              <TranslatableText text="Student Life & Activities" />
            </h3>
            <div className="space-y-3">
              {studentLifeLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center text-sm ${isDark ? 'text-dark-text-secondary hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'} transition-colors hover:translate-x-1 transform duration-200`}
                >
                  <link.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                  <TranslatableText text={link.name} />
                </Link>
              ))}
            </div>
          </div>

          {/* Resources & Contact Section */}
          <div>
            <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-dark-text' : 'text-gray-900'} flex items-center`}>
              <DocumentTextIcon className="w-5 h-5 mr-2" style={{ color: siteBranding.primary_color }} />
              <TranslatableText text="Resources & Contact" />
            </h3>
            <div className="space-y-3">
              {resourcesContactLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center text-sm ${isDark ? 'text-dark-text-secondary hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'} transition-colors hover:translate-x-1 transform duration-200`}
                >
                  <link.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                  <TranslatableText text={link.name} />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* School Information Section */}
        <div className={`mt-12 pt-8 border-t ${isDark ? 'border-dark-border' : 'border-gray-200'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* School Info */}
            <div>
              <motion.div 
                className="flex items-center space-x-3 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {siteBranding.logo_url ? (
                  <img 
                    src={siteBranding.logo_url} 
                    alt={siteBranding.site_name}
                    className="w-12 h-12 object-contain rounded-lg"
                    style={{ maxWidth: '100%', height: 'auto' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                ) : (
                  <img 
                    src="https://i.postimg.cc/bNdvMYR5/pm-shri-seeklogo.png" 
                    alt="PM SHRI Logo"
                    className="w-12 h-12 object-contain rounded-lg"
                    style={{ maxWidth: '100%', height: 'auto' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                )}
                
                {/* Fallback icon - only shown when image fails to load */}
                <div 
                  className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full hidden items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${siteBranding.primary_color}, ${siteBranding.secondary_color})`
                  }}
                >
                  <AcademicCapIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                    <TranslatableText text={siteBranding.site_name} />
                  </h3>
                  <p className="text-sm" style={{ color: siteBranding.primary_color }}>
                    <TranslatableText text={siteBranding.site_tagline} />
                  </p>
                </div>
              </motion.div>
              
              <p className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-4 text-sm leading-relaxed`}>
                <TranslatableText text="Excellence in education, building tomorrow's leaders today. Empowering young minds with quality education, modern facilities, and holistic development under the PM SHRI initiative." />
              </p>
            </div>

            {/* Contact Information */}
            <div>
              <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                <TranslatableText text="Contact Information" />
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: siteBranding.primary_color }} />
                  <span className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} text-sm`}>
                    PM SHRI GGSSS FARMANA (44337) KHARKHODA, SONIPAT, HARYANA, 131408
                  </span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="w-5 h-5 flex-shrink-0" style={{ color: siteBranding.primary_color }} />
                  <span className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} text-sm`}>
                    +91-9255429697
                  </span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="w-5 h-5 flex-shrink-0" style={{ color: siteBranding.primary_color }} />
                  <span className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} text-sm`}>
                    pmshriggsssfarmana@gmail.com
                  </span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: siteBranding.primary_color }} />
                  <span className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} text-sm`}>
                    Mon - Sat: 8:00 AM - 2:30 PM<br />
                    Closed on Sunday
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className={`border-t ${isDark ? 'border-dark-border bg-dark-bg' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-500'} text-sm text-center md:text-left`}>
              © 2026 <TranslatableText text="PM SHRI GGSSS FARMANA (44337)" />. <TranslatableText text="All rights reserved" /> | 
              <TranslatableText text=" Powered by" /> 
              <a href="https://ffstudios.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 ml-1">
                FutureForge Studios
              </a>
            </p>
            
            <div className="flex items-center space-x-3">
              {socialMediaLinks.map((link) => (
                <motion.a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 ${getSocialMediaColor(link.platform)} rounded-full flex items-center justify-center text-white transition-colors`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  title={`Follow us on ${link.platform}`}
                >
                  <span className="text-sm font-bold">{getSocialMediaIcon(link.platform)}</span>
                </motion.a>
              ))}
              
              {/* Show default links if no social media links are configured */}
              {socialMediaLinks.length === 0 && (
                <>
                  <motion.a
                    href="#"
                    className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-sm font-bold">f</span>
                  </motion.a>
                  
                  <motion.a
                    href="#"
                    className="w-10 h-10 bg-blue-400 rounded-full flex items-center justify-center text-white hover:bg-blue-500 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-sm font-bold">t</span>
                  </motion.a>
                  
                  <motion.a
                    href="#"
                    className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-700 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-sm font-bold">yt</span>
                  </motion.a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;